import { isTokenValid } from "@/services/utils/helpers/tokenUtils";
import axios from "axios";

// --- Request Cancellation ---
let abortController = new AbortController();

export const cancelAllRequests = () => {
  abortController.abort(); // Abort all pending requests
  abortController = new AbortController(); // Create a new controller for future requests
};
// --------------------------

// const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

const BASE_URL = "https://api.investhive.io";

// ============================================
// CSRF Token Management
// ============================================
let csrfToken = null;

export const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/api/v1/csrf-token`);
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    return null;
  }
};

// Fetch CSRF token on module load
fetchCsrfToken();

// Create axios instance
const apiClient = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token and cancellation signal
apiClient.interceptors.request.use(
  (config) => {
    // Add the abort signal to the request=
    config.signal = abortController.signal;

    const token = localStorage.getItem("authToken");

    // Only validate if token exists
    if (token) {
      if (isTokenValid(token)) {
        config.headers["Authorization"] = `Bearer ${token}`;
      } else {
        // If token exists but is invalid, remove it
        localStorage.removeItem("authToken");
      }
    }

    // Add CSRF token to state-changing requests
    if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase())) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    // Do not reject cancellation errors, as they are expected
    if (axios.isCancel(error)) {
      return Promise.resolve({ data: { cancelled: true } }); // Resolve with a special object
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token-related errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 403 with ACCOUNT_SUSPENDED code - DON'T remove token
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.code === "ACCOUNT_SUSPENDED"
    ) {

      // Add suspended flag to error for easy handling
      error.isSuspended = true;
      error.suspendedMessage = error.response.data.message;

      // Don't remove token or redirect - just return the error
      return Promise.reject(error);
    }

    // Handle other 401 (Unauthorized) or 403 (Forbidden) responses
    if (
      error.response &&
      (error.response.status === 401 ||
        (error.response.status === 403 &&
          error.response.data?.code !== "ACCOUNT_SUSPENDED"))
    ) {
      // Token expired or invalid
      // 1. Clear ALL sensitive data
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("kyc_id");
      localStorage.removeItem("kyc_form_data");

      // 2. Redirect to login if not already there
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        const publicPaths = ["/Signin-Signup", "/login", "/register", "/", "/blog", "/about", "/contact"];

        // Check if current path is NOT public
        const isPublicPath = publicPaths.some(path =>
          path === "/" ? currentPath === "/" : currentPath.startsWith(path)
        );

        if (!isPublicPath) {
          // Use replace to prevent "Back" button from returning to protected page
          window.location.replace("/Signin-Signup");
        }
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient, BASE_URL };
